/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { useAppLoadingContext } from "../../Context/AppLoadingContext";
import { AppLoadingContextTypes } from "../../Context/ContextTypes/AppLoadingContextTypes";
import { Box, Button, Grid, Link, Typography } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import parse from "html-react-parser";
import { retrieveDemandRates } from "../../services/DemandRatesService";
import AnimatedNumber from "./AnimatedNumber/AnimatedNumber";
import theme, { colors } from "../../styles/themes/ctfs-theme";

import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";

type InterestRateSectionPropTypes = {
  interestRateTitle: string;
  interestRateDescription: string;
  interestRateButtonText: string;
  interestRateButtonUrl: string;
  interestRateButtonAriaLabel: string;
  interestRateLinkText: string;
  interestRateLinkUrl: string;
  interestRateLinkAriaLabel: string;
  interestRateParagraph: string;
  interestRateBackgroundGradients: string;
  interestRateImage: string;
  interestRateImageAltText: string;
  interestRateImageWidth: string;
  interestRateImageHeight: string;
  isInEditor: boolean;
  cqPath: string;
  cqType: string;
};

export default function InterestRateSectionReact(props: InterestRateSectionPropTypes) {
  const [rate, setRate] = useState("");
  const isSmallScreen = useMediaQuery((theme: any) => theme.breakpoints.down("sm"));
  // translation
  const { translationsData } = useAppLoadingContext() as AppLoadingContextTypes;
  const DomPurify = (tag: string) => {
    return DOMPurify.sanitize(translationsData[tag]);
  };

  useEffect(() => {
    callApi();
  }, []);

  const callApi = async () => {
    const rate = await retrieveDemandRates({
      category: "DMDS",
      fund: "CAD",
    });
    setRate(rate);
  };
  const translatedData = {
    // title and button
    interestRateTitle: DomPurify(props.interestRateTitle),
    interestRateDescription: parse(DomPurify(props.interestRateDescription)),
    interestRateButtonText: DomPurify(props.interestRateButtonText),
    interestRateButtonUrl: DomPurify(props.interestRateButtonUrl),
    interestRateButtonAriaLabel: DomPurify(props.interestRateButtonAriaLabel),
    interestRateLinkText: DomPurify(props.interestRateLinkText),
    interestRateLinkUrl: DomPurify(props.interestRateLinkUrl),
    interestRateLinkAriaLabel: DomPurify(props.interestRateLinkAriaLabel),
    interestRateParagraph: parse(DomPurify(props.interestRateParagraph)),
    interestRateBackgroundGradients: DomPurify(props.interestRateBackgroundGradients),
    interestRateImage: DomPurify(props.interestRateImage),
    interestRateImageAltText: DomPurify(props.interestRateImageAltText),
  };

  return (
    <Box
      component="section"
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Grid
        container
        display="flex"
        flexDirection="row"
        justifyContent="center"
        maxWidth={{ xs: "95%", sm: "95%", md: "95%", lg: "1296px" }}
        paddingTop={{ xs: "0rem", sm: "0rem", md: "0rem", lg: "2rem" }}
        marginLeft={{ xs: "auto", sm: "auto", md: "auto", lg: "auto" }}
        marginRight={{ xs: "auto", sm: "auto", md: "auto", lg: "auto" }}
        sx={{
          backgroundImage: props?.interestRateBackgroundGradients,
          borderRadius: "40px 40px 0 0",
          overflow: "auto",
          background: `linear-gradient(149deg, ${colors.NeutralGrey3} 27%, #d9d9d9 97% )`,
        }}
      >
        <Grid
          item
          xs={12}
          lg={5}
          justifyContent="center"
          alignItems="center"
          display="flex"
          paddingLeft={{ xs: "0px", sm: "0px", md: "0px", lg: "80px" }}
        >
          <Grid
            container
            direction="column"
            padding={{ xs: "5% 28px 0% 28px", sm: "5% 18% 0% 18%", md: "5% 18% 0% 18%", lg: "0%" }}
          >
            <Typography
              variant="h2"
              display="block"
              fontWeight="700"
              textAlign={{ xs: "center", sm: "center", md: "center", lg: "left" }}
            >
              <AnimatedNumber rate={rate} text={translatedData.interestRateTitle} />
            </Typography>

            <Typography
              variant="body1"
              display="block"
              textAlign={{ xs: "center", sm: "center", md: "center", lg: "left" }}
              fontSize={{ xs: "19px", sm: "19px", md: "19px", lg: "19px" }}
              paddingRight={{ xs: "0px", sm: "0px", md: "0px", lg: "80px" }}
              margin="10px 0px"
            >
              {translatedData.interestRateDescription}
            </Typography>

            <Grid
              container
              item
              textAlign={{ xs: "center", sm: "center", md: "center", lg: "left" }}
              margin="10px 0px"
              justifyContent={{ lg: "flex-start", xs: "center" }}
              alignItems="center"
            >
              <Button
                component={Link}
                variant="contained"
                href={translatedData.interestRateButtonUrl}
                className="apply-now-btn"
                sx={{
                  width: { lg: "243px", sm: "292px", xs: "100%" },
                  height: { lg: "56px", sm: "46px", xs: "44px%" },
                  textTransform: "capitalize",
                  "&:focus-visible": {
                    outline: "3px solid blue",
                    outlineOffset: "3px",
                    zIndex: "1",
                  },
                  fontSize: "16px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {translatedData.interestRateButtonText}
                <LazyLoadImage
                  effect="blur"
                  src="https://media.ctfs.com/pages/CRECRD/CRECRD_External_Action.svg"
                  aria-hidden={true}
                  alt=""
                  style={{
                    marginLeft: "11px",
                    display: "flex",
                    width: "16px",
                    height: "16px",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                  role={"presentation"}
                />
              </Button>
            </Grid>
            <Grid
              item
              display="flex"
              alignItems="center"
              justifyContent={{ xs: "center", sm: "center", md: "center", lg: "left" }}
              textAlign={{ xs: "center", sm: "center", md: "center", lg: "left" }}
              margin="10px 0px"
            >
              <Typography
                variant="body1"
                component={Link}
                href={translatedData.interestRateLinkUrl}
                sx={{
                  textDecorationColor: colors.NeutralSoftBlack,
                  textUnderlineOffset: "5px",
                  color:colors.NeutralSoftBlack + ` !important`,
                  fontSize:"18px !important",
                  display:"flex"
                }}
              >
                {translatedData.interestRateLinkText}
                <img
                    loading="lazy"
                    fetchpriority="low"
                    src="https://media.ctfs.com/pages/CRECRD/Chevron-Right.svg"
                    alt=""
                    width="24"
                    height="24"
                    style={{
                      scale: "1",
                      margin: "0px 0px 0px 5px",
                      padding:"2px 0px 0px 0px"
                    }} 
                  />
              </Typography>
            </Grid>

            <Typography
              variant="body1"
              display="block"
              textAlign={{ xs: "center", sm: "center", md: "center", lg: "left" }}
              margin="10px 0px"
              fontSize="12px"
              sx={{ color: "#505050", fontStyle: "italic", lineHeight: "normal" }}
            >
              {translatedData.interestRateParagraph}
            </Typography>
          </Grid>
        </Grid>

        {/* card image container */}
        <Grid item xs={12} sm={5} md={5} lg={7} display="flex" justifyContent="center">
          <LazyLoadImage
            effect="blur"
            src={translatedData.interestRateImage}
            style={{
              width: props.interestRateImageWidth ? props.interestRateImageWidth : "auto",
              height: props.interestRateImageHeight ? props.interestRateImageHeight : "auto",
              display: "flex",
            }}
            alt={translatedData.interestRateImageAltText}
          />
        </Grid>
      </Grid>

      {/* triangle color borders */}
      <Grid container xs={12} sm={12} md={12} lg={12}>
        <Box
          component="div"
          className="trianleBgBorder"
          sx={{ height: { xs: "16px", lg: "32px" } }}
        />
      </Grid>
    </Box>
  );
}
